import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BcmApi2Service } from '@modules/bcm/@shared/services';

export interface OnlineBookingStripeStatus {
    stripeConnectAccountId: string | null;
    stripeConnectOnboardingComplete: boolean;
    publicBookingSubdomain: string | null;
    publicBookingUrl: string | null;
    serviceFeeFlat: number;
    serviceFeePercent: number;
}

export interface OnlineBookingStripeOnboardingResponse {
    onboardingUrl?: string;
    stripeConnectAccountId: string;
    alreadyComplete?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class OnlineBookingApiService extends BcmApi2Service {

    getStripeStatus(): Observable<OnlineBookingStripeStatus> {
        this.suppressErrorForNextRequest();
        return super.get<OnlineBookingStripeStatus>(`online-booking/stripe/status?_=${Date.now()}`);
    }

    startStripeOnboarding(): Observable<OnlineBookingStripeOnboardingResponse> {
        this.suppressErrorForNextRequest();
        return super.post<OnlineBookingStripeOnboardingResponse>('online-booking/stripe/onboarding', {});
    }

    updateSubdomain(publicBookingSubdomain: string): Observable<{ publicBookingSubdomain: string; publicBookingUrl: string }> {
        this.suppressErrorForNextRequest();
        return super.put<{ publicBookingSubdomain: string; publicBookingUrl: string }>('online-booking/subdomain', { publicBookingSubdomain });
    }

    getWebsiteUrl(publicBookingSubdomain: string): Observable<{ publicBookingUrl: string | null }> {
        this.suppressErrorForNextRequest();
        return super.get<{ publicBookingUrl: string | null }>(
            `online-booking/website-url?publicBookingSubdomain=${encodeURIComponent(publicBookingSubdomain)}`,
        );
    }

    getBerthQrCode(berthId: number): Observable<BerthOnlineBookingQrInfo> {
        return super.get<BerthOnlineBookingQrInfo>(`online-booking/berths/${berthId}/qr-code`);
    }

    generateBerthQrCode(berthId: number): Observable<BerthOnlineBookingQrInfo> {
        return super.post<BerthOnlineBookingQrInfo>(`online-booking/berths/${berthId}/qr-code`, {});
    }

    getBerthQrBulkStatus(): Observable<BerthQrBulkStatus> {
        this.suppressErrorForNextRequest();
        return super.get<BerthQrBulkStatus>('online-booking/berths/qr-codes/bulk/status');
    }

    startBerthQrBulkGenerate(mode: BerthQrBulkGenerateMode): Observable<BerthQrBulkJobState> {
        return super.post<BerthQrBulkJobState>('online-booking/berths/qr-codes/bulk/generate', { mode });
    }

    cancelBerthQrBulkGenerate(): Observable<BerthQrBulkJobState> {
        return super.post<BerthQrBulkJobState>('online-booking/berths/qr-codes/bulk/cancel', {});
    }

    downloadBerthQrBulkZip(): Observable<Blob> {
        return super.getBlob('online-booking/berths/qr-codes/bulk/download');
    }
}

export type BerthQrBulkGenerateMode = 'missing' | 'all';

export interface BerthQrBulkJobFailure {
    berthId: number;
    berthHandle: string;
    message: string;
}

export interface BerthQrBulkJobState {
    jobId: string;
    status: 'running' | 'completed' | 'failed' | 'cancelled';
    mode: BerthQrBulkGenerateMode;
    total: number;
    processed: number;
    succeeded: number;
    failed: number;
    currentBerthHandle?: string | null;
    startedAt: string;
    finishedAt?: string | null;
    failures: BerthQrBulkJobFailure[];
}

export interface BerthQrBulkStatus {
    totalBerths: number;
    berthsWithQr: number;
    berthsMissingQr: number;
    lastBulkRunAt?: string | null;
    currentStickerSignature: string;
    lastBulkStickerSignature?: string | null;
    stickersOutdated: boolean;
    activeJob?: BerthQrBulkJobState | null;
}

export interface BerthOnlineBookingQrInfo {
    berthId: number;
    berthHandle?: string;
    publicUrl?: string;
    qrCodeFileId?: number;
    qrCodePayload?: string;
    qrCodeCategory?: string;
}
